import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getSession, getSignedDoc, deleteSession, retryTopSession, type Session } from "../api/client";
import { StatusBadge } from "../components/StatusBadge";

const TIMELINE_STEPS = [
  { key: "created", label: "Creata", icon: "add_circle" },
  { key: "collecting", label: "Raccolta dati", icon: "chat" },
  { key: "compiled", label: "PDF compilato", icon: "description" },
  { key: "signing", label: "In firma", icon: "draw" },
  { key: "signed", label: "Firmato", icon: "verified" },
];

const STEP_ORDER = TIMELINE_STEPS.map((s) => s.key);

const POLL_INTERVAL = 5000; // 5s auto-refresh
const TERMINAL_STATUSES = new Set(["signed", "failed"]);

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo: string = (location.state as { returnTo?: string })?.returnTo ?? "/sessions";
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retryingTop, setRetryingTop] = useState(false);
  const [polling, setPolling] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const fetchSession = useCallback(async () => {
    if (!id) return;
    try {
      const s = await getSession(id);
      setSession(s);
      if (TERMINAL_STATUSES.has(s.status)) setPolling(false);
    } catch (e) {
      setError((e as Error).message);
      setPolling(false);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (!polling || !id) return;

    timerRef.current = setInterval(fetchSession, POLL_INTERVAL);

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerRef.current);
      } else {
        fetchSession();
        timerRef.current = setInterval(fetchSession, POLL_INTERVAL);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [polling, id, fetchSession]);

  const handleDownload = async () => {
    if (!id) return;
    setDownloading(true);
    try {
      const blob = await getSignedDoc(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signed_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const handleRetry = () => {
    if (!id) return;
    navigate(`/upload?retry=${id}`);
  };

  const handleRetryTop = async () => {
    if (!id || !confirm("Verrà creato un nuovo dossier su TOP e inviato un nuovo link di firma all'utente. Procedere?")) return;
    setRetryingTop(true);
    try {
      const updated = await retryTopSession(id);
      setSession(updated);
      setPolling(true);
    } catch (e) {
      alert("Errore ritentativo TOP: " + (e as Error).message);
    } finally {
      setRetryingTop(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm("Vuoi eliminare definitivamente questa sessione? L'azione è irreversibile.")) return;
    setDeleting(true);
    try {
      await deleteSession(id);
      navigate(returnTo);
    } catch (e) {
      alert("Errore: " + (e as Error).message);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-8)" }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div>
        <button className="btn btn-ghost" onClick={() => navigate(returnTo)}>
          <span className="material-symbols-outlined">arrow_back</span> {returnTo.startsWith("/providers") ? "Torna al provider" : "Torna alle sessioni"}
        </button>
        <div className="toast toast-error" style={{ marginTop: "var(--space-3)" }}>
          <span className="material-symbols-outlined">error</span>
          {error || "Sessione non trovata"}
        </div>
      </div>
    );
  }

  const currentIdx = STEP_ORDER.indexOf(session.status);
  const isFailed = session.status === "failed";

  const prefilled = (typeof session.prefilled_data === "object" && session.prefilled_data)
    ? session.prefilled_data : {};
  const fieldsToCollect: string[] = Array.isArray(session.fields_to_collect)
    ? session.fields_to_collect : [];
  const collected = (typeof session.collected_data === "object" && session.collected_data)
    ? session.collected_data : {};

  const prefilledEntries = Object.entries(prefilled);
  const collectedEntries = Object.entries(collected);
  const collectedCount = collectedEntries.length;
  const totalToCollect = fieldsToCollect.length;

  return (
    <div>
      <button
        className="btn btn-ghost"
        onClick={() => navigate(returnTo)}
        style={{ marginBottom: "var(--space-4)" }}
      >
        <span className="material-symbols-outlined">arrow_back</span> {returnTo.startsWith("/providers") ? "Torna al provider" : "Torna alle sessioni"}
      </button>

      {/* Header card */}
      <div className="card">
        <div className="card-header">
          <h1 className="section-title" style={{ margin: 0 }}>
            Sessione per {session.recipient_name}
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            {polling && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)" }}>
                <span className="spinner" style={{ width: 12, height: 12 }} /> Live
              </span>
            )}
            <StatusBadge status={session.status} />
          </div>
        </div>

        <div className="detail-grid">
          <div>
            <span className="detail-label">Telefono</span>
            <span className="detail-value">{session.recipient_phone}</span>
          </div>
          {session.company_name && (
            <div>
              <span className="detail-label">Azienda</span>
              <span className="detail-value">{session.company_name}</span>
            </div>
          )}
          <div>
            <span className="detail-label">Riconoscimento</span>
            <span className="detail-value">{session.recognition_type || "—"}</span>
          </div>
          <div>
            <span className="detail-label">Creata</span>
            <span className="detail-value">{fmtDate(session.created_at)}</span>
          </div>
          {session.updated_at && session.updated_at !== session.created_at && (
            <div>
              <span className="detail-label">Ultimo aggiornamento</span>
              <span className="detail-value">{fmtDate(session.updated_at)}</span>
            </div>
          )}
          {session.signed_at && (
            <div>
              <span className="detail-label">Firmato</span>
              <span className="detail-value">{fmtDate(session.signed_at)}</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {session.status !== "signed" && (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: "var(--space-2)", marginTop: "var(--space-4)",
            paddingTop: "var(--space-4)", borderTop: "1px solid var(--indaco-gray-200)",
          }}>
            {(session.status === "failed" || session.status === "signing") && session.compiled_file_path && (
              <button
                className="btn btn-primary"
                onClick={handleRetryTop}
                disabled={retryingTop || deleting}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                title="Ricrea il dossier TOP e genera un nuovo link di firma (i dati raccolti vengono mantenuti)"
              >
                {retryingTop ? <span className="spinner" /> : <span className="material-symbols-outlined" style={{ fontSize: 18 }}>replay</span>}
                {retryingTop ? "Ritentativo in corso…" : "Ritenta firma TOP"}
              </button>
            )}
            <button
              className="btn btn-outline"
              onClick={handleRetry}
              disabled={deleting || retryingTop}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
              Riavvia sessione
            </button>
            <button
              className="btn btn-outline"
              onClick={handleDelete}
              disabled={deleting || retryingTop}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--indaco-error)", borderColor: "var(--indaco-error)" }}
            >
              {deleting ? <span className="spinner" /> : <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>}
              {deleting ? "Eliminazione…" : "Elimina"}
            </button>
          </div>
        )}
      </div>

      {/* Timeline + Signing actions — side by side on wide screens */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)", marginTop: "var(--space-4)" }}>
        {/* Timeline */}
        <div className="card" style={{ margin: 0 }}>
          <h2 className="section-title">Avanzamento</h2>
          <div className="timeline">
            {TIMELINE_STEPS.map((step, idx) => {
              let cls = "timeline-step";
              if (isFailed) {
                cls += idx <= currentIdx ? " timeline-step-done" : "";
                if (idx === currentIdx) cls = "timeline-step timeline-step-failed";
              } else {
                if (idx < currentIdx) cls += " timeline-step-done";
                if (idx === currentIdx) cls += " timeline-step-active";
              }
              return (
                <div key={step.key} className={cls}>
                  <div className="timeline-icon">
                    <span className="material-symbols-outlined">{step.icon}</span>
                  </div>
                  <span className="timeline-label">{step.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick info + actions */}
        <div className="card" style={{ margin: 0 }}>
          <h2 className="section-title">Riferimenti</h2>
          <div style={{ fontSize: "var(--font-size-sm)", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--indaco-gray-500)" }}>Session ID</span>
              <span style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)", wordBreak: "break-all", textAlign: "right", maxWidth: "60%" }}>{session.id}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--indaco-gray-500)" }}>Document ID</span>
              <span style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)", wordBreak: "break-all", textAlign: "right", maxWidth: "60%" }}>{session.document_id}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--indaco-gray-500)" }}>Agent thread</span>
              <span style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)", wordBreak: "break-all", textAlign: "right", maxWidth: "60%" }}>{session.agent_thread_id || "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--indaco-gray-500)" }}>Dossier TOP</span>
              <span style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)" }}>{session.dossier_id || "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--indaco-gray-500)" }}>PDF compilato</span>
              <span>{session.compiled_file_path ? "✅" : "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--indaco-gray-500)" }}>PDF firmato</span>
              <span>{session.signed_file_path ? "✅" : "—"}</span>
            </div>
          </div>

          {/* Signing URL + Download inline */}
          {(session.signing_url || session.status === "signed") && (
            <div style={{ marginTop: "var(--space-4)", paddingTop: "var(--space-3)", borderTop: "1px solid var(--indaco-gray-200)", display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              {session.signing_url && session.status !== "signed" && (
                <a href={session.signing_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ fontSize: "var(--font-size-sm)" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>open_in_new</span>
                  Link di firma
                </a>
              )}
              {session.status === "signed" && (
                <button className="btn btn-success" onClick={handleDownload} disabled={downloading} style={{ fontSize: "var(--font-size-sm)" }}>
                  {downloading
                    ? <><span className="spinner" /> Download…</>
                    : <><span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span> Scarica firmato</>
                  }
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data collection — collapsible with progress summary always visible */}
      <details className="card" style={{ marginTop: "var(--space-4)" }} open={collectedCount > 0 && collectedCount < totalToCollect}>
        <summary style={{ cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>assignment</span>
            <strong style={{ fontSize: "var(--font-size-base)" }}>Raccolta dati</strong>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            {/* Mini progress bar */}
            {totalToCollect > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 80, height: 6, borderRadius: 3, background: "var(--indaco-gray-100)", overflow: "hidden", display: "inline-block" }}>
                  <span style={{
                    display: "block", height: "100%", borderRadius: 3, transition: "width 0.4s ease",
                    width: `${totalToCollect > 0 ? Math.round((collectedCount / totalToCollect) * 100) : 0}%`,
                    background: collectedCount >= totalToCollect ? "var(--indaco-success)" : "var(--indaco-primary)",
                  }} />
                </span>
                <span style={{
                  fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)",
                  color: collectedCount >= totalToCollect ? "var(--indaco-success)" : "var(--indaco-primary)",
                  whiteSpace: "nowrap",
                }}>
                  {collectedCount}/{totalToCollect}
                </span>
              </span>
            )}
          </span>
        </summary>

        <div style={{ marginTop: "var(--space-4)" }}>
          {totalToCollect > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--indaco-gray-200)" }}>
                  <th style={{ textAlign: "left", padding: "var(--space-2)", color: "var(--indaco-gray-500)", fontWeight: "var(--font-weight-medium)" }}>Campo</th>
                  <th style={{ textAlign: "left", padding: "var(--space-2)", color: "var(--indaco-gray-500)", fontWeight: "var(--font-weight-medium)" }}>Stato</th>
                  <th style={{ textAlign: "left", padding: "var(--space-2)", color: "var(--indaco-gray-500)", fontWeight: "var(--font-weight-medium)" }}>Valore</th>
                </tr>
              </thead>
              <tbody>
                {fieldsToCollect.map((fieldName) => {
                  const val = collected[fieldName];
                  const hasValue = val !== undefined && val !== null && val !== "";
                  return (
                    <tr key={fieldName} style={{ borderBottom: "1px solid var(--indaco-gray-100)" }}>
                      <td style={{ padding: "var(--space-2)", fontWeight: "var(--font-weight-medium)" }}>{fieldName}</td>
                      <td style={{ padding: "var(--space-2)" }}>
                        {hasValue ? (
                          <span style={{ color: "var(--indaco-success)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span> Raccolto
                          </span>
                        ) : (
                          <span style={{ color: "var(--indaco-gray-400)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>pending</span> In attesa
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "var(--space-2)", color: hasValue ? "var(--indaco-gray-800)" : "var(--indaco-gray-300)" }}>
                        {hasValue ? String(val) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-500)" }}>
              Tutti i campi sono stati precompilati dall'operatore.
            </p>
          )}
        </div>
      </details>

      {/* Prefilled data — collapsible */}
      {prefilledEntries.length > 0 && (
        <details className="card" style={{ marginTop: "var(--space-4)" }}>
          <summary style={{ cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>edit_note</span>
            <strong style={{ fontSize: "var(--font-size-base)" }}>Dati precompilati</strong>
            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-500)", marginLeft: "auto" }}>
              {prefilledEntries.length} campi
            </span>
          </summary>
          <div className="detail-grid" style={{ marginTop: "var(--space-4)" }}>
            {prefilledEntries.map(([k, v]) => (
              <div key={k}>
                <span className="detail-label">{k}</span>
                <span className="detail-value">{v || "—"}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
